import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, MenuItem,
  Skeleton, Snackbar, Stack, TextField, Typography,
} from '@mui/material';
import { BsDownload } from 'react-icons/bs';

import axiosInstance from '../../axiosInstance';
import Fiche from '../Expert/Fiche';
import ChampDate from '../ChampDate/ChampDate';

const VIDE = { reference: '', startDate: '', referenceCG: '', compagnie_id: '' };

const liste = (data) => (Array.isArray(data) ? data : (data.results || []));

/** Le contrat d'assurance et les conditions générales dont il relève. */
function EditContract() {
  const { expertiseId } = useParams();

  const [contrat, setContrat] = useState(VIDE);
  const [initial, setInitial] = useState(VIDE);
  const [compagnies, setCompagnies] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [chargementCG, setChargementCG] = useState(false);
  const [erreur, setErreur] = useState('');
  const [enregistre, setEnregistre] = useState(false);
  const [occupe, setOccupe] = useState(false);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const [fiche, societes] = await Promise.all([
        axiosInstance.get(`/contract/${expertiseId}/`),
        axiosInstance.get('referential/compagnies/'),
      ]);
      const lu = {
        reference: fiche.data.reference || '',
        startDate: (fiche.data.startDate || '').split('T')[0],
        referenceCG: fiche.data.referenceCG || '',
        compagnie_id: fiche.data.compagnie ? fiche.data.compagnie.id : '',
      };
      setContrat(lu);
      setInitial(lu);
      setCompagnies(liste(societes.data));
      setErreur('');
    } catch {
      setErreur("Le contrat n'a pas pu être chargé.");
    } finally {
      setChargement(false);
    }
  }, [expertiseId]);

  useEffect(() => { charger(); }, [charger]);

  // Les conditions générales suivent la compagnie : en changer sans recharger
  // proposerait les conditions d'un autre assureur.
  useEffect(() => {
    if (chargement) return;
    setChargementCG(true);
    const url = contrat.compagnie_id
      ? `referential/general-conditions/?compagnie=${contrat.compagnie_id}`
      : 'referential/general-conditions/';
    axiosInstance.get(url)
      .then(({ data }) => setConditions(liste(data)))
      .catch(() => setConditions([]))
      .finally(() => setChargementCG(false));
  }, [contrat.compagnie_id, chargement]);

  const modifier = (champ) => (e) => {
    setContrat((etat) => ({ ...etat, [champ]: e.target.value }));
    if (erreur) setErreur('');
  };

  const modifie = JSON.stringify(contrat) !== JSON.stringify(initial);

  const fichierCG = conditions.find(
    (c) => c.name === contrat.referenceCG && c.fichier);

  const soumettre = async (e) => {
    e.preventDefault();
    setOccupe(true);
    setErreur('');
    try {
      await axiosInstance.patch(`/contract/${expertiseId}/`, {
        reference: contrat.reference,
        startDate: contrat.startDate || null,
        referenceCG: contrat.referenceCG,
        compagnie_id: contrat.compagnie_id || null,
      });
      setInitial(contrat);
      setEnregistre(true);
    } catch {
      setErreur("Les modifications n'ont pas pu être enregistrées.");
    } finally {
      setOccupe(false);
    }
  };

  if (chargement) {
    return <Stack spacing={2}>
      <Skeleton height={56} /><Skeleton height={56} /><Skeleton height={140} />
    </Stack>;
  }

  return (
    <Box component="form" onSubmit={soumettre} noValidate>
      <Stack spacing={3}>
        {erreur && <Alert severity="error">{erreur}</Alert>}

        <Fiche titre="Le contrat">
          <Stack spacing={2.5}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5}>
              <TextField
                label="Référence du contrat"
                value={contrat.reference}
                onChange={modifier('reference')}
                disabled={occupe}
                helperText="Le numéro de police."
              />
              <ChampDate
                label="Date de souscription"
                value={contrat.startDate}
                onChange={modifier('startDate')}
                disabled={occupe}
                InputLabelProps={{ shrink: true }}
                helperText=" "
              />
            </Stack>

            <TextField
              select
              label="Compagnie d'assurance"
              value={contrat.compagnie_id}
              onChange={modifier('compagnie_id')}
              disabled={occupe}
              helperText={compagnies.length === 0
                ? "Aucune compagnie au référentiel."
                : "Elle détermine les conditions générales proposées."}
            >
              <MenuItem value=""><em>Aucune</em></MenuItem>
              {compagnies.map((compagnie) => (
                <MenuItem key={compagnie.id} value={compagnie.id}>
                  {compagnie.name}
                  {compagnie.country ? ` (${compagnie.country})` : ''}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </Fiche>

        <Fiche
          titre="Conditions générales"
          explication="Le document qui dit ce que le contrat couvre. C'est lui que l'on citera face à la compagnie."
          actions={chargementCG ? <CircularProgress size={18} />
            : conditions.length > 0 && (
              <Chip size="small" label={`${conditions.length} au référentiel`} />
            )}
        >
          <Stack spacing={2}>
            <Autocomplete
              freeSolo
              options={conditions.map((c) => c.name)}
              value={contrat.referenceCG}
              onInputChange={(_, valeur) =>
                setContrat((etat) => ({ ...etat, referenceCG: valeur || '' }))}
              disabled={occupe}
              renderInput={(params) => (
                <TextField {...params} label="Nom des conditions générales"
                           helperText={conditions.length === 0 && !chargementCG
                             ? (contrat.compagnie_id
                               ? "Rien au référentiel pour cette compagnie — vous pouvez saisir le nom."
                               : "Choisissez d'abord une compagnie.")
                             : "Choisissez dans la liste, ou saisissez un nom."} />
              )}
            />

            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                component="a"
                href={fichierCG ? fichierCG.fichier : undefined}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                size="small"
                startIcon={<BsDownload />}
                disabled={!fichierCG}
              >
                Ouvrir le document
              </Button>
              {!fichierCG && contrat.referenceCG && (
                <Typography variant="caption" color="text.secondary">
                  Aucun fichier joint à ces conditions.
                </Typography>
              )}
            </Stack>
          </Stack>
        </Fiche>

        <Stack direction="row" spacing={1.5} justifyContent="flex-end">
          <Button onClick={() => setContrat(initial)}
                  disabled={occupe || !modifie}>
            Annuler les modifications
          </Button>
          <Button type="submit" variant="contained"
                  disabled={occupe || !modifie}>
            {occupe ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </Stack>
      </Stack>

      <Snackbar
        open={enregistre}
        autoHideDuration={3000}
        onClose={() => setEnregistre(false)}
        message="Contrat enregistré"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}

export default EditContract;
